import { useState, useMemo, useCallback } from 'react'

export interface TableSelection {
  selectedIds: string[]
  isSelected: (id: string) => boolean
  toggleRow: (id: string) => void
  toggleAll: () => void
  isAllSelected: boolean
  hasSelection: boolean
  clearSelection: () => void
}

export const useTableSelection = (
  allIds: readonly string[]
): TableSelection => {
  const [selectedMap, setSelectedMap] = useState<Record<string, boolean>>({})

  const selectedSet = useMemo(
    () => new Set(allIds.filter((id) => selectedMap[id])),
    [allIds, selectedMap]
  )

  const selectedIds = useMemo(() => Array.from(selectedSet), [selectedSet])
  const hasSelection = selectedSet.size > 0
  const isAllSelected = allIds.length > 0 && selectedSet.size === allIds.length
  const isSelected = (id: string) => selectedSet.has(id)

  const toggleRow = useCallback((id: string) => {
    setSelectedMap((prev) => {
      const next = { ...prev }
      if (next[id]) {
        delete next[id]
      } else {
        next[id] = true
      }
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setSelectedMap((prev) => {
      const currentCount = allIds.reduce((n, id) => (prev[id] ? n + 1 : n), 0)
      const allSelected = allIds.length > 0 && currentCount === allIds.length
      if (allSelected) return {}
      return allIds.reduce<Record<string, boolean>>((acc, id) => {
        acc[id] = true
        return acc
      }, {})
    })
  }, [allIds])

  const clearSelection = useCallback(() => setSelectedMap({}), [])

  return {
    selectedIds,
    isSelected,
    toggleRow,
    toggleAll,
    isAllSelected,
    hasSelection,
    clearSelection
  }
}
